import { Helmet } from "react-helmet-async";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Stack,
  Button,
  MenuItem,
  Container,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import Iconify from "../components/iconify";
import { get, post, put } from "src/utils/networkUtils";
import DivisionLeaderboardDialog from "src/components/stage-admin/DivisionLeaderboardDialog";
import StagePromotionDialog from "src/components/stage-admin/StagePromotionDialog";
import DivisionLeaguesTable from "src/components/stage-admin/DivisionLeaguesTable";
import { AppWidgetSummary } from "src/sections/@dashboard/app";
import useWithUrlTenant from "src/hooks/useTenant";
import PromotionCheck from "src/components/promotions/PromotionCheck";
import PromotionProgress from "src/components/promotions/PromotionProgress";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1, paddingTop: 3 }}>{children}</Box>}
    </div>
  );
}

export default function DivisionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [createdRows, setCreatedRows] = useState(null);
  const [archivedRows, setArchivedRows] = useState(null);
  const [openStageDialog, setOpenStageDialog] = useState(false);
  const [stage, setStage] = useState(null);
  const [promotionPeriod, setPromotionPeriod] = useState(null);
  const [divisions, setDivisions] = useState(null);
  const [promotionStatus, setPromotionStatus] = useState(null);
  const [divisionsStats, setDivisionsStats] = useState(null);
  const [selectedDivisionId, setSelectedDivisionId] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedDivisionLeagueId, setSelectedDivisionLeagueId] =
    useState(null);
  const [readyToPromoteCount, setReadyToPromoteCount] = useState(null);
  const [divisionsWaitingForLeagues, setDivisionsWaitingForLeagues] =
    useState(null);
  const [value, setValue] = useState(0);
  const LIMIT = 10;
  const [pageActive, setPageActive] = useState(0);
  const [pageCreated, setPageCreated] = useState(0);
  const [pageArchived, setPageArchived] = useState(0);

  const [hasMoreActive, setHasMoreActive] = useState(true);
  const [hasMoreCreated, setHasMoreCreated] = useState(true);
  const [hasMoreArchived, setHasMoreArchived] = useState(true);
  const observer = useRef();
  const withTenant = useWithUrlTenant();
  const [searchParams] = useSearchParams();

  // Get tenantId from URL params for tracking changes
  const tenantId = searchParams.get("tenantId");

  // Get tenants from Redux store and find current tenant
  const tenants = useSelector((state) => state.tenant.tenants);
  const currentTenant = tenants?.find((tenant) => tenant.tenantId === parseInt(tenantId));
  const tenantName = currentTenant?.name || "";

  const lastRowRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (value === 0 && hasMoreActive) setPageActive((prev) => prev + 1);
          if (value === 1 && hasMoreCreated) setPageCreated((prev) => prev + 1);
          if (value === 2 && hasMoreArchived)
            setPageArchived((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMoreActive, hasMoreCreated, hasMoreArchived, value]
  );

  useEffect(() => {
    get(withTenant(`/admin/divisions`)).then(function (response) {
      setDivisions(response.divisions);
      setDivisionsStats(response.stats);
      setPromotionStatus(response.stats.promotionStatus);
      const promotionStatus = response.stats.promotionStatus;
      if (response.divisions.length > 0) {
        let minorsDivision;
        minorsDivision = response.divisions.find(
          (item) => item.divisionLevel === 1
        );
        if (!minorsDivision) {
          minorsDivision = response.divisions[0];
        }

        setSelectedDivisionId(minorsDivision.divisionId);
        setSelectedDivision(minorsDivision);
        setReadyToPromoteCount(minorsDivision.stats.readyLeaguesCount);

        setDivisionsWaitingForLeagues(
          response.divisions
            .filter((item) => item.stats.usersWaitingDivisionLevelCount > 0)
            .map((item) => item.divisionName)
        );
        get(withTenant(`/admin/stage-periods?offset=0&limit=${LIMIT}`)).then(
          function (response) {
            if (promotionStatus.status === "Ready") {
              const selectedStagePeriod = response.stagePeriods.find((item) => {
                if (item.stagePeriodId === promotionStatus.stagePeriodId) {
                  return item;
                }
                return null;
              });
              setPromotionPeriod(selectedStagePeriod);
            }
          }
        );
      }
    });
  }, [withTenant, tenantId]);

  useEffect(() => {
    if (value === 0 && selectedDivisionId) {
      get(
        withTenant(
          `/admin/divisions/${selectedDivisionId}/leagues?offset=${pageActive * LIMIT
          }&limit=${LIMIT}`
        )
      ).then((response) => {
        if (pageActive === 0) {
          setRows(response.leagues);
        } else {
          setRows((prev) => [...prev, ...response.leagues]);
        }
        if (response.leagues.length < LIMIT) setHasMoreActive(false);
        setLoading(false);
      });
    }
  }, [pageActive, selectedDivisionId, value, withTenant]);

  useEffect(() => {
    if (value === 1 && selectedDivisionId) {
      get(
        withTenant(
          `/admin/divisions/${selectedDivisionId}/leagues?filter=created&offset=${pageCreated * LIMIT
          }&limit=${LIMIT}`
        )
      ).then((response) => {
        if (pageCreated === 0) {
          setCreatedRows(response.leagues);
        } else {
          setCreatedRows((prev) => [...prev, ...response.leagues]);
        }
        if (response.leagues.length < LIMIT) setHasMoreCreated(false);
        setLoading(false);
      });
    }
  }, [pageCreated, selectedDivisionId, value, withTenant]);

  useEffect(() => {
    if (value === 2 && selectedDivisionId) {
      get(
        withTenant(
          `/admin/divisions/${selectedDivisionId}/leagues?filter=archived&offset=${pageArchived * LIMIT
          }&limit=${LIMIT}`
        )
      ).then((response) => {
        if (pageArchived === 0) {
          setArchivedRows(response.leagues);
        } else {
          setArchivedRows((prev) => [...prev, ...response.leagues]);
        }
        if (response.leagues.length < LIMIT) setHasMoreArchived(false);
        setLoading(false);
      });
    }
  }, [pageArchived, selectedDivisionId, value, withTenant]);

  const handlePeriodChange = (event) => {
    setSelectedDivisionId(event.target.value);
    const selectedDivision = divisions.find(
      (division) => division.divisionId === event.target.value
    );
    setReadyToPromoteCount(selectedDivision?.stats?.readyLeaguesCount ?? null);
    setSelectedDivision(selectedDivision ?? null);
    setRows([]);
    setCreatedRows([]);
    setArchivedRows([]);

    setPageActive(0);
    setPageCreated(0);
    setPageArchived(0);

    setHasMoreActive(true);
    setHasMoreCreated(true);
    setHasMoreArchived(true);
  };

  const cancelPromotionHandler = () => {
    put(withTenant(`/admin/promotion-status`), {
      stagePeriodId: 1,
      status: "None",
    }).then(function () {
      window.location.reload(true);
    });
  };

  const handleCloseStageDialog = () => {
    setStage(null);
    setOpenStageDialog(false);
  };
  const handleCloseLeaderboardDialog = () => {
    setSelectedDivisionLeagueId(null);
  };

  const handleSuccessStagePromotionDialog = (stagePeriodId) => {
    setLoading(true);
    put(withTenant(`/admin/promotion-status`), {
      stagePeriodId,
      status: "Ready",
    }).then(function (response) {
      window.location.reload(true);
    });
  };
  const handlePromoteDivisionLeaguesHandler = () => {
    setPromotionLoading(true);
    post(withTenant(`/admin/divisions/${selectedDivisionId}/promote`)).then(function (
      response
    ) {
      setLoading(false);
      window.location.reload(true);
    });
  };
  const handleCompletePromotionHandler = () => {
    setPromotionLoading(true);
    post(withTenant(`/admin/complete-promotion`)).then(function (response) {
      setLoading(false);
      window.location.reload(true);
    });
  };
  const handleCreateDivisionLeaguesHandler = () => {
    setPromotionLoading(true);
    post(withTenant(`/admin/divisions/${selectedDivisionId}/create-leagues`)).then(
      function (response) {
        setLoading(false);
        window.location.reload(true);
      }
    );
  };

  const handleChangeTabs = (event, newValue) => {
    setValue(newValue);
  };

  return promotionLoading || loading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <>
      <Helmet>
        <title>{tenantName ? `${tenantName} - Divisions` : "Divisions"}</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          {promotionPeriod ? (
            <Typography variant="h4" gutterBottom>
              Promoting Divisions for: {promotionPeriod.name}
            </Typography>
          ) : (
            <Typography variant="h4" gutterBottom>
              {tenantName ? `${tenantName} - Divisions` : "Divisions"}
            </Typography>
          )}
          <Box>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:settings-2-outline" />}
              onClick={() => navigate(`/dashboard/divisions/settings?tenantId=${tenantId}`)}
              sx={{ mr: 2 }}
            >
              Settings
            </Button>

            {promotionStatus && promotionStatus.status === "None" && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:play-circle-outline" />}
                onClick={() => setOpenStageDialog(true)}
                sx={{ mr: 2 }}
              >
                Start Promotion Process
              </Button>
            )}
            {promotionStatus && promotionStatus.status === "Ready" && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:slash-outline" />}
                  onClick={() => cancelPromotionHandler()}
                  sx={{ mr: 2 }}
                >
                  Cancel Promotion Process
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:save-outline" />}
                  onClick={() => handleCompletePromotionHandler()}
                  sx={{ mr: 2 }}
                >
                  Finish Promotion Process
                </Button>
              </>
            )}
          </Box>
        </Stack>
        {divisions && divisionsStats !== null &&
          divisionsStats?.promotionStatus &&
          divisionsStats?.promotionStatus.status === "Ready" && (
            <PromotionCheck divisions={divisions} stagePeriodId={divisionsStats?.promotionStatus.stagePeriodId} />
          )}
        {divisions && divisionsStats !== null &&
          divisionsStats?.promotionStatus &&
          divisionsStats?.promotionStatus.status === "Ready" && (
            <PromotionProgress divisions={divisions} />
          )}

        {divisionsStats !== null &&
          divisionsStats?.promotionStatusStats?.inProgress > 0 && (
            <Alert sx={{ marginBottom: 2 }} severity="error">
              <Typography variant="h5" gutterBottom>
                ERROR: there is leagues{" "}
                {`(${divisionsStats?.promotionStatusStats?.inProgress})`} with
                uncompleted promotion
              </Typography>
              <Typography variant="body1" gutterBottom>
                It could be parallel process running. Otherwise - ask developers
                to fix the issue.
              </Typography>
            </Alert>
          )}
        {promotionStatus && promotionStatus.status === "Ready" && (
          <Alert sx={{ marginBottom: 2 }} severity="warning">
            <Typography variant="h5" gutterBottom>
              Promotion Process is enabled
            </Typography>
            <Typography variant="body1" gutterBottom>
              Make sure all leagues promotion COMPLETED and Leagues Created
              before STOP the process
            </Typography>
          </Alert>
        )}
        {divisionsWaitingForLeagues !== null &&
          divisionsWaitingForLeagues.length > 0 &&
          promotionStatus &&
          promotionStatus.status === "Ready" && (
            <Alert sx={{ marginBottom: 2 }} severity="warning">
              <Typography variant="h5" gutterBottom>
                Divisions Waiting For Leagues to create:
              </Typography>
              <Typography variant="body1" gutterBottom>
                {divisionsWaitingForLeagues.join(", ")}
              </Typography>
            </Alert>
          )}
        {divisionsStats !== null &&
          promotionStatus &&
          promotionStatus.status === "Ready" && (
            <Grid container>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Alert sx={{ marginBottom: 2 }} severity="info">
                  <Grid container spacing={4} sx={{ ml: 2 }}>
                    <Grid item>
                      {` READY TO PROMOTE: ${divisionsStats?.promotionStatusStats?.ready} `}
                    </Grid>
                    <Grid item>
                      {`PROMOTION IN PROGRESS: ${divisionsStats?.promotionStatusStats?.inProgress} `}
                    </Grid>
                    <Grid item>
                      {`PROMOTION COMPLETED: ${divisionsStats?.promotionStatusStats?.completed} `}
                    </Grid>
                  </Grid>
                </Alert>
              </Grid>
            </Grid>
          )}

        {divisions !== null && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid
              item
              xs={
                promotionStatus && promotionStatus.status === "Ready" ? 6 : 12
              }
              sx={{ mt: 2 }}
            >
              <FormControl fullWidth>
                <InputLabel id="week-select-label">Division</InputLabel>
                <Select
                  labelId="week-select-label"
                  id="week-select"
                  value={selectedDivisionId}
                  label="Select Division"
                  onChange={handlePeriodChange}
                >
                  <MenuItem key={"default"} value={null} disabled={true}>
                    <Grid container spacing={2} sx={{ ml: 2 }}>
                      <Grid
                        item
                        xs={6}
                        sx={{ fontWeight: "bold", fontSize: "1.2em" }}
                      ></Grid>
                      <Grid item xs={2} sx={{ textAlign: "center" }}>
                        Leagues
                      </Grid>
                      <Grid item xs={2} sx={{ textAlign: "center" }}>
                        Members
                      </Grid>
                      {promotionStatus && promotionStatus.status === "Ready" && (
                        <Grid item xs={2} sx={{ textAlign: "center" }}>
                          Promoted
                        </Grid>
                      )}
                    </Grid>
                  </MenuItem>
                  {divisions &&
                    divisions.map((division) => (
                      <MenuItem
                        key={division.divisionId}
                        value={division.divisionId}
                      >
                        <Grid container spacing={2} sx={{ ml: 2 }}>
                          <Grid
                            item
                            xs={6}
                            sx={{ fontWeight: "bold", fontSize: "1.2em" }}
                          >
                            {division.divisionName}
                          </Grid>
                          <Grid item xs={2} sx={{ textAlign: "center" }}>
                            {division.stats.activeLeaguesCount}
                          </Grid>
                          <Grid item xs={2} sx={{ textAlign: "center" }}>
                            {division.stats.membershipCount}
                          </Grid>
                          {promotionStatus &&
                            promotionStatus.status === "Ready" && (
                              <Grid item xs={2} sx={{ textAlign: "center" }}>
                                {promotionStatus.promotedDivisions.includes(
                                  division.divisionId
                                ) ? (
                                  <Iconify
                                    icon={"eva:checkmark-circle-2-outline"}
                                    sx={{ color: "green" }}
                                  />
                                ) : (
                                  <Iconify
                                    icon={"eva:close-circle-outline"}
                                    sx={{ color: "red" }}
                                  />
                                )}
                              </Grid>
                            )}
                        </Grid>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            {promotionStatus && promotionStatus.status === "Ready" && (
              <Grid item xs={3} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!readyToPromoteCount}
                  sx={{ height: "100%" }}
                  startIcon={<Iconify icon="eva:arrow-upward-outline" />}
                  onClick={() => handlePromoteDivisionLeaguesHandler()}
                >
                  Promote/Relegate
                </Button>
              </Grid>
            )}
            {promotionStatus && promotionStatus.status === "Ready" && (
              <Grid item xs={3} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={
                    !selectedDivision ||
                    selectedDivision?.stats?.usersWaitingDivisionLevelCount ===
                    0
                  }
                  sx={{ height: "100%" }}
                  startIcon={<Iconify icon="eva:plus-outline" />}
                  onClick={() => handleCreateDivisionLeaguesHandler()}
                >
                  Assign Leagues
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        {selectedDivision && (
          <Grid container spacing={3} sx={{ mb: 2 }} justifyContent={"center"}>
            <Grid item xs={4} md={2} lg={2}>
              <AppWidgetSummary
                title={`Active Leagues (Ready: ${selectedDivision?.stats?.readyLeaguesCount ?? 0
                  })`}
                total={selectedDivision?.stats?.activeLeaguesCount ?? 0}
                icon={"eva:shield-outline"}
                color="info"
              />
            </Grid>
            <Grid item xs={4} md={2} lg={2}>
              <AppWidgetSummary
                title="Active Leagues Members"
                total={selectedDivision?.stats?.membershipCount ?? 0}
                icon={"eva:person-outline"}
                color="info"
              />
            </Grid>
            <Grid item xs={4} md={2} lg={2}>
              <AppWidgetSummary
                title="Overall Division Users"
                total={selectedDivision?.stats?.usersDivisionLevelCount ?? 0}
                icon={"eva:person-outline"}
                color="info"
              />
            </Grid>
            {promotionStatus && promotionStatus.status === "Ready" && (
              <Grid item xs={2}>
                <AppWidgetSummary
                  title="Users Waiting For League"
                  total={
                    promotionStatus && promotionStatus.status === "Ready"
                      ? selectedDivision?.stats?.usersWaitingDivisionLevelCount
                      : 0
                  }
                  icon={"eva:person-outline"}
                  color="warning"
                />
              </Grid>
            )}
            {promotionStatus && promotionStatus.status === "Ready" && (
              <Grid item xs={2}>
                <AppWidgetSummary
                  title="Leagues Waiting For Publishing"
                  total={selectedDivision?.stats?.createdLeaguesCount ?? 0}
                  icon={"eva:shield-outline"}
                  color="warning"
                />
              </Grid>
            )}
          </Grid>
        )}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
            >
              <Tab label="Active Leagues" {...a11yProps(0)} />
              <Tab label="Ready To Publish" {...a11yProps(1)} />
              <Tab label="Archived" {...a11yProps(2)} />
            </Tabs>
          </Box>
          <TabPanel value={value} index={0}>
            <DivisionLeaguesTable
              rows={rows}
              onLeaderboardClick={(leagueId) =>
                setSelectedDivisionLeagueId(leagueId)
              }
              showLeaderboard={true}
              lastRowRef={lastRowRef}
            />
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            <DivisionLeaguesTable
              rows={createdRows}
              onLeaderboardClick={(leagueId) =>
                setSelectedDivisionLeagueId(leagueId)
              }
              showLeaderboard={true}
              lastRowRef={lastRowRef}
            />
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </TabPanel>
          <TabPanel value={value} index={2}>
            <DivisionLeaguesTable
              rows={archivedRows}
              onLeaderboardClick={(leagueId) =>
                setSelectedDivisionLeagueId(leagueId)
              }
              showLeaderboard={true}
              lastRowRef={lastRowRef}
            />
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </TabPanel>
        </Card>
      </Container>

      {openStageDialog && (
        <StagePromotionDialog
          stage={stage}
          open={openStageDialog}
          onSuccessHandler={handleSuccessStagePromotionDialog}
          onClose={handleCloseStageDialog}
        />
      )}
      {selectedDivisionLeagueId !== null && (
        <DivisionLeaderboardDialog
          divisionLeagueId={selectedDivisionLeagueId}
          open={selectedDivisionLeagueId !== null}
          onClose={handleCloseLeaderboardDialog}
        />
      )}
    </>
  );
}
